import { useBulkPutChoicesApi, usePostChoiceApi, usePutChoiceApi } from 'api/choice'
import { Flex } from 'components/shared/flex'
import { Attribute, CheckboxField, InputField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { Button, Modal, Table } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm, usePrevious } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Choice, ChoiceForm } from 'entities/choice'
import { MasterDataContext } from 'contexts/master_data_context'
import { NestedModal, TableCell } from 'components/shared/widget'
import lodash from 'lodash'
import { BiPlus } from 'react-icons/bi'

const FormView = (form: Form<Choice>, apiError: ApiError) => {
    return (
        <>
            <InputField apiError={apiError} form={form} attr={'tableName'} label="TableName" required />
            <Flex>
                <InputField apiError={apiError} form={form} attr={'parentColumn'} label="ParentColumn" />
                <InputField apiError={apiError} form={form} attr={'parentValue'} label="ParentValue" />
            </Flex>
            <Flex>
                <InputField apiError={apiError} form={form} attr={'column'} label="column" />
                <InputField apiError={apiError} form={form} attr={'value'} label="value" />
            </Flex>
        </>
    )
}

type NewChoiceFormProps = {
    onComplete?: () => void
    type: string
}

export const NewChoiceForm = (props: NewChoiceFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostChoiceApi()
    const form = useForm<ChoiceForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Choiceの登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>{FormView(form, api.apiError)}</>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

type EditChoiceFormProps = {
    choice: Choice
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditChoiceForm = (props: EditChoiceFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutChoiceApi()
    const form = useForm<ChoiceForm>(props.choice)

    useEffectSkipFirst(() => {
        form.set(props.choice)
    }, [props.choice])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>選択肢の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <>{FormView(form, api.apiError)}</>}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

type BulkEditChoiceFormProps = {
    choices: Choice[]
    show: boolean
    onClose: () => void
    onComplete?: () => void
    tableName: string
    column: string
    parentColumn?: string
    parentValue?: string
    showDisabled: boolean
    setShowDisabled: Dispatch<SetStateAction<boolean>>
}

const getAttr = <T extends unknown>(attr: string, index?: number): Attribute<T> => {
    if (typeof index === 'number') {
        return [index, attr]
    } else {
        return attr as keyof T
    }
}

export const BulkEditChoiceForm = (props: BulkEditChoiceFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const masterData = useContext(MasterDataContext)
    const api = useBulkPutChoicesApi()
    const form = useForm<ChoiceForm[]>(props.choices)
    const allInputForm = useForm<ChoiceForm>({})
    const previousAllInputForm = usePrevious<ChoiceForm>({})

    useEffect(() => {
        if (props.parentColumn) {
            allInputForm.updateObject('parentColumn', lodash.snakeCase(props.parentColumn))
        }
    }, [])

    useEffectSkipFirst(() => {
        allInputForm.updateObject('parentValue', props.parentValue)
    }, [props.parentValue])

    useEffectSkipFirst(() => {
        form.set(props.choices)
    }, [props.choices])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
                masterData.reloadChoices()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        api.clearApiError()
        props.onClose()
    }

    const bulkInput = (attr: keyof ChoiceForm) => {
        const copy = [...form.object]
        copy.forEach((choice: ChoiceForm) => {
            const value: any = allInputForm.object[attr] as any
            choice[attr] = value
        })
        form.set(copy)
    }

    useEffectSkipFirst(() => {
        const diff: ChoiceForm = lodash.omitBy(allInputForm.object, (v, k) => previousAllInputForm[k as keyof ChoiceForm] === v)
        Object.keys(diff).forEach((k: string) => {
            bulkInput(k as keyof ChoiceForm)
        })
    }, [allInputForm.object])

    const formRow = <T extends unknown>(form: Form<T>, apiError: ApiError, index?: number): JSX.Element => {
        return (
            <>
                <td>
                    <InputField apiError={apiError} form={form} attr={getAttr('value', index)} index={index} />
                </td>
                <td>
                    <CheckboxField apiError={apiError} form={form} attr={getAttr('default', index)} index={index} checkboxLabel="デフォルト" />
                </td>
                <td>
                    <CheckboxField apiError={apiError} form={form} attr={getAttr('disabled', index)} index={index} checkboxLabel="非表示" />
                </td>
            </>
        )
    }

    return (
        <NestedModal show={props.show} onHide={handleClose} aria-labelledby="modal-choice-form" size="lg">
            <Modal.Header>
                <Modal.Title>
                    {props.column} {props.parentColumn ? `(親: ${props.parentColumn} / ${props.parentValue})` : ''}
                </Modal.Title>
                <div
                    className="linked-text"
                    style={{ marginLeft: 10 }}
                    onClick={() => {
                        if (props.showDisabled) {
                            props.setShowDisabled(false)
                        } else {
                            props.setShowDisabled(true)
                        }
                    }}
                >
                    {!props.showDisabled ? 'すべて表示' : '非表示を隠す'}
                </div>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                {form.object.length > 0 ? (
                    <div>
                        <Table className="sticky-table">
                            <thead>
                                <tr>
                                    <TableCell th minWidth={120}>
                                        選択肢
                                    </TableCell>
                                    <TableCell th maxWidth={80}>
                                        デフォルト
                                    </TableCell>
                                    <TableCell th maxWidth={80}>
                                        非表示
                                    </TableCell>
                                </tr>
                            </thead>
                            <tbody>
                                {form.object.map((choice: Choice, index: number) => {
                                    return (
                                        <tr key={`bulk-update-choice-${choice.id}`} className="vertical-align-middle-row">
                                            {formRow(form, api.apiError, index)}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                        <Flex></Flex>
                    </div>
                ) : (
                    <div style={{ margin: 30, width: '100%', textAlign: 'center' }}>選択肢を追加してください</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', margin: 'auto', width: 200 }}>
                    <Button
                        onClick={() => {
                            form.update((f) => {
                                f.push({
                                    tableName: props.tableName,
                                    column: lodash.snakeCase(props.column),
                                    parentColumn: props.parentColumn || undefined,
                                    parentValue: props.parentValue || undefined,
                                })
                            })
                        }}
                    >
                        <BiPlus />
                        <span style={{ marginLeft: 5 }}>追加</span>
                    </Button>
                </div>
                <Button variant="secondary" onClick={handleClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </NestedModal>
    )
}
